import Vue from 'vue'
import { pick } from 'lodash-es'
import configs from '@/config'
import store from '@/store'
import SystemConnection, { CONNECTION_STATUS } from '@/utils/system/SystemConnection'
import clientSubscription from '@/utils/client.subscription'
import constants from '@/constant'
import { logger } from '@ahaslides-product/frontend-toolkit'

const logFn = logger.extend('answersApi')
const ANSWER_LOG_FIELDS = ['presentation', 'presentationId', 'presentationVersion', 'slide', 'slideId', 'slideVersion', 'audience', 'accessCode', 'type', 'slideType', 'createdBy', 'vote', 'timeoutAnswer']
let resource = null
const actions = {
  getMyQuizAnswers: {
    method: 'GET',
    url: `${configs.baseUrl}/api/answers/quiz?presentationId={presentationId}&audienceId={audienceId}`,
  },
  createWordCloudAnswerViaLiveProxy: {
    method: 'POST',
    url: `${configs.liveProxyUrl}/api/live/answers/wordcloud`,
  },
  createWordCloudAnswerViaLiveProxyNewKafka: {
    method: 'POST',
    url: `${configs.liveProxyUrl}/api/${constants.LiveProxyRootPath}/answers/wordcloud`,
  },
  createWordCloudAnswerViaLiveApi: {
    method: 'POST',
    url: `${configs.baseUrlLiveApi}/api/stream/answers/wordcloud`,
  },
  createWordCloudAnswerV1: {
    method: 'POST',
    url: `${configs.baseUrl}/api/slide-option/audience-create-wordcloud`,
  },
  createPollAnswerViaLiveProxy: {
    method: 'POST',
    url: `${configs.liveProxyUrl}/api/live/answers/poll`,
  },
  createPollAnswerViaLiveProxyNewKafka: {
    method: 'POST',
    url: `${configs.liveProxyUrl}/api/${constants.LiveProxyRootPath}/answers/poll`,
  },
  createPollAnswerViaLiveApi: {
    method: 'POST',
    url: `${configs.baseUrlLiveApi}/api/stream/answers/poll`,
  },
  createPollAnswerV1: {
    method: 'POST',
    url: `${configs.baseUrl}/api/answer/create`,
  },
  createScaleAnswerViaLiveProxy: {
    method: 'POST',
    url: `${configs.liveProxyUrl}/api/live/answers/scale`,
  },
  createScaleAnswerViaLiveProxyNewKafka: {
    method: 'POST',
    url: `${configs.liveProxyUrl}/api/${constants.LiveProxyRootPath}/answers/scale`,
  },
  createScaleAnswerViaLiveApi: {
    method: 'POST',
    url: `${configs.baseUrlLiveApi}/api/stream/answers/scale`,
  },
  createScaleAnswerV1: {
    method: 'POST',
    url: `${configs.baseUrl}/api/answer/create`,
  },
  createQuizAnswer: {
    method: 'POST',
    url: `${configs.baseUrl}/api/answer/create`,
  },
  createQuizAnswerThroughProxy: {
    method: 'POST',
    url: `${configs.liveProxyUrl}/api/live/answer/create`,
  },
  createQuizAnswerThroughProxyV2: {
    method: 'POST',
    url: `${configs.liveProxyUrl}/api/live/answer/create/v2`,
  },
  createQuizAnswerThroughProxyNewKafka: {
    method: 'POST',
    url: `${configs.liveProxyUrl}/api/${constants.LiveProxyRootPath}/answer/create/v2`,
  },
  createQuizAnswerGeneralApiV3: {
    method: 'POST',
    url: `${configs.baseUrl}/api/answer/create/v3`,
  },
}

const initResource = () => {
  resource = Vue.resource('', {}, actions)
}

const getMyQuizAnswers = async ({ presentationId, audienceId }, params) => {
  const res = await resource.getMyQuizAnswers({ presentationId, audienceId }, params)

  return res.body
}

/**
 * Extract query params from payload to append as querystring
 * for tracking purpose. We want to know which answer submission
 * belongs to which slide
 * @param {*} payload
 * @returns
 */
const extractQueryParamsFromPayload = (payload) => {
  try {
    if (!store.getters['logger/canTrackSlideIdForPostReqs'] || !payload) {
      return undefined
    }

    const slideId = payload.slideId || (['string', 'number'].includes(typeof payload.slide) ? payload.slide : undefined)
    const audience = typeof payload.audience === 'string' ? payload.audience : undefined
    if (slideId || audience) {
      return {
        ...(slideId ? { slideId } : {}),
        ...(audience ? { audience } : {}),
      }
    }

    return undefined
  } catch (err) {
    logFn('extractQueryParamsFromPayload', err)
    return undefined
  }
}

// Auto-retry config for answer submissions. A flaky connection used to silently
// drop answers; we now retry transient network failures before giving up.
const MAX_RETRY = 1 // up to 1 retry => 2 attempts in total
const RETRY_DELAY_MS = 200

const delay = (ms) => {
  // eslint-disable-next-line promise/avoid-new
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

// Kill-switch for the answer-submission resilience fix. When off, submissions
// make a single attempt and failures keep the legacy (silent) error shape.
const canUseSubmitAnswerRetry = () => (
  store.getters['featureToggles/canUseSubmitAnswerRetry']
)

/**
 * Build the result returned when a submission ultimately fails.
 * With the flag on, the failure is flagged (`isSubmitError`) so the caller can
 * surface it and let the audience retry. With the flag off, we return the legacy
 * shape (no `isSubmitError`), which the caller treats as a non-failure.
 * @param {*} error error/response object thrown by vue-resource
 * @returns {object}
 */
const buildSubmitError = (error) => {
  if (!canUseSubmitAnswerRetry()) {
    return { body: { error: error && error.message } }
  }
  return {
    body: { error: error && error.message },
    isSubmitError: true,
    errorCode: error && error.errorCode,
    status: error && error.status,
  }
}

/**
 * Only retry failures caused by the network, not deterministic client errors.
 * - `!status`      -> request never received a response (offline, DNS/TCP/TLS, timeout)
 * - `status >= 500` -> server-side error worth retrying
 * 4xx client errors (e.g. 411 submission closed) are deterministic and are NOT retried.
 * @param {*} error error/response object thrown by vue-resource
 * @returns {boolean}
 */
const isRetryableNetworkError = (error) => {
  const status = error && error.status
  return !status || status >= 500
}

/**
 * Run an async request with automatic retry on transient network errors.
 * Every failed attempt and the response are logged; the error is re-thrown once retries are exhausted
 * or the failure is not network-related.
 * @param {() => Promise<*>} requestFn function performing the request
 * @param {string} context label used for logging
 * @param {object} logFields ids logged with the response
 * @returns {Promise<*>} the resolved request value
 */
const withNetworkRetry = async (requestFn, context, logFields) => {
  // Flag off => single attempt (no retry), matching the pre-fix behavior.
  const maxRetry = canUseSubmitAnswerRetry() ? MAX_RETRY : 0
  const startedAt = Date.now()
  for (let attempt = 0; attempt <= maxRetry; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const res = await requestFn()
      logFn(context, {
        ...logFields, status: res.status, ms: Date.now() - startedAt, attempt: attempt + 1,
      })
      return res
    } catch (error) {
      const willRetry = attempt < maxRetry && isRetryableNetworkError(error)
      logFn(`${context} request failed`, {
        attempt: attempt + 1,
        maxAttempts: maxRetry + 1,
        status: error && error.status,
        message: error && error.message,
        willRetry,
      })
      if (!willRetry) {
        throw error
      }
      // eslint-disable-next-line no-await-in-loop
      await delay(RETRY_DELAY_MS * (attempt + 1))
    }
  }
  // Unreachable: the loop above either returns a value or throws.
  return undefined
}

// Invoke a resource action by name, preserving `resource` as the receiver, and
// appending query params for tracking when available.
const sendAnswer = (methodName, queryParams, payload) => (
  queryParams ? resource[methodName](queryParams, payload) : resource[methodName](payload)
)

const createWordCloudAnswer = async (payload) => {
  const startedAt = Date.now()
  const logFields = pick(payload, ANSWER_LOG_FIELDS)
  try {
    const queryParams = extractQueryParamsFromPayload(payload)
    let methodName = 'createWordCloudAnswerV1'
    if (store.getters['liveStatsV2/canUseWordCloudLiveProxy']) {
      methodName = 'createWordCloudAnswerViaLiveProxy'
    } else if (store.getters['liveStatsV2/canUseWordCloudLiveApi']) {
      methodName = 'createWordCloudAnswerViaLiveApi'
    }
    // No auto-retry: wordcloud submissions are not idempotent, so retrying after a
    // lost response could create a duplicate answer. We still surface the failure
    // (buildSubmitError) so the audience can choose to retry.
    const res = await sendAnswer(methodName, queryParams, payload)
    logFn('createWordCloudAnswer', {
      ...logFields, status: res.status, ms: Date.now() - startedAt, attempt: 1,
    })
    return res.body
  } catch (error) {
    logFn('createWordCloudAnswer error', {
      ...logFields,
      status: error && error.status,
      message: error && error.message,
      ms: Date.now() - startedAt,
    })
    SystemConnection.detectGeneralConnection(CONNECTION_STATUS.UNSTABLE)
    return buildSubmitError(error)
  }
}

const createPollAnswer = async (payload) => {
  const startedAt = Date.now()
  const logFields = pick(payload, ANSWER_LOG_FIELDS)
  try {
    const queryParams = extractQueryParamsFromPayload(payload)
    let methodName = 'createPollAnswerV1'
    if (store.getters['liveStatsV2/canUsePollLiveProxy']) {
      methodName = 'createPollAnswerViaLiveProxy'
    } else if (store.getters['liveStatsV2/canUsePollLiveApi']) {
      methodName = 'createPollAnswerViaLiveApi'
    }
    // No auto-retry: poll submissions are not idempotent (a retry after a lost
    // response could create a duplicate vote). The failure is still surfaced via
    // buildSubmitError so the audience can choose to retry.
    const res = await sendAnswer(methodName, queryParams, payload)
    logFn('createPollAnswer', {
      ...logFields, status: res.status, ms: Date.now() - startedAt, attempt: 1,
    })
    return res.body
  } catch (error) {
    logFn('createPollAnswer error', {
      ...logFields,
      status: error && error.status,
      message: error && error.message,
      ms: Date.now() - startedAt,
    })
    SystemConnection.detectGeneralConnection(CONNECTION_STATUS.UNSTABLE)
    return buildSubmitError(error)
  }
}

const createScaleAnswer = async (payload) => {
  const startedAt = Date.now()
  const logFields = pick(payload, ANSWER_LOG_FIELDS)
  try {
    const queryParams = extractQueryParamsFromPayload(payload)
    let methodName = 'createScaleAnswerV1'
    if (store.getters['liveStatsV2/canUseScaleLiveProxy']) {
      methodName = 'createScaleAnswerViaLiveProxy'
    } else if (store.getters['liveStatsV2/canUseScaleLiveApi']) {
      methodName = 'createScaleAnswerViaLiveApi'
    }
    // No auto-retry: scale submissions are not idempotent (a retry after a lost
    // response could create a duplicate answer). The failure is still surfaced via
    // buildSubmitError so the audience can choose to retry.
    const res = await sendAnswer(methodName, queryParams, payload)
    logFn('createScaleAnswer', {
      ...logFields, status: res.status, ms: Date.now() - startedAt, attempt: 1,
    })
    return res.body
  } catch (error) {
    logFn('createScaleAnswer error', {
      ...logFields,
      status: error && error.status,
      message: error && error.message,
      ms: Date.now() - startedAt,
    })
    SystemConnection.detectGeneralConnection(CONNECTION_STATUS.UNSTABLE)
    return buildSubmitError(error)
  }
}

const createQuizAnswer = async (payload) => {
  const startedAt = Date.now()
  const logFields = pick(payload, ANSWER_LOG_FIELDS)
  try {
    const queryParams = extractQueryParamsFromPayload(payload)
    // V2 only sends a socket message, and a copy deck is never presented, so nothing would save its answers.
    if (store.getters['liveStatsV2/canUseLiveStatsForQuizV2'] && !store.getters['replay/isRetakePlayer']) {
      // Only the POST is retried; the result subscription is set up once afterwards.
      await withNetworkRetry(() => (queryParams
        ? resource.createQuizAnswerThroughProxyV2(queryParams, payload)
        : resource.createQuizAnswerThroughProxyV2(payload)), 'createQuizAnswer', logFields)
      const key = `${clientSubscription.constantObjectSubcriptionType.submitQuizAnswerType}-${payload.slideId}`
      return clientSubscription.subscribe(key)
    }

    const res = await withNetworkRetry(() => (queryParams
      ? resource.createQuizAnswer(queryParams, payload)
      : resource.createQuizAnswer(payload)), 'createQuizAnswer', logFields)
    return res.body
  } catch (error) {
    logFn('createQuizAnswer error', {
      ...logFields,
      status: error && error.status,
      message: error && error.message,
      ms: Date.now() - startedAt,
    })
    SystemConnection.detectGeneralConnection(CONNECTION_STATUS.UNSTABLE)
    return buildSubmitError(error)
  }
}

export default {
  initResource,
  getMyQuizAnswers,
  createWordCloudAnswer,
  createScaleAnswer,
  createPollAnswer,
  createQuizAnswer,
}
